class QuickAddColorPicker extends HTMLElement {
  constructor() {
    super();
    this.productCard = this.closest('.product-card-grid');
    this.addEventListener('change', this.onSwatchChange.bind(this));
  }

  onSwatchChange(event) {
    // Find the actual input element - event.target might be the label
    const selectedInput = event.target.matches('[data-color-option]') 
      ? event.target 
      : this.querySelector('input[data-color-option]:checked');
    
    if (!selectedInput || !selectedInput.hasAttribute('data-color-option')) return;

    this.updateProductCard(selectedInput);
  }

  updateProductCard(selectedInput) {
    if (!this.productCard) return;

    const productUrl = selectedInput.dataset.productUrl;
    const productMedia = this.parseJSON(selectedInput.dataset.productMedia);
    const productTitle = selectedInput.dataset.productTitle;
    const productId = selectedInput.dataset.productId;
    const firstVariantId = selectedInput.dataset.productFirstVariant;

    if (productUrl) {
      this.updateProductLink(productUrl);
      this.updateQuickAddButton(productUrl, productId, firstVariantId);
    }

    if (productMedia && productMedia.length > 0) {
      this.updateProductImages(productMedia, productTitle);
    }

    this.updateSelectedState();
  }

  updateQuickAddButton(productUrl, productId, variantId) {
    // Update the quick add button's data-product-url so it fetches the correct product
    const quickAddButton = this.productCard.querySelector('[data-product-url]');
    if (quickAddButton) {
      quickAddButton.setAttribute('data-product-url', productUrl);
    }

    // Update variant ID for single-variant products (direct add to cart)
    const variantInput = this.productCard.querySelector('input[name="id"].product-variant-id');
    if (variantInput && variantId) {
      variantInput.value = variantId;
      // Check if variant is available and enable/disable accordingly
      // This would require fetching variant data, but for now we'll just update the ID
    }

  }

  updateProductLink(url) {
    const link = this.productCard.querySelector('[data-product-card-link]');
    if (link) {
      link.href = url;
    }
  }

  updateProductImages(mediaArray, productTitle) {
    const imageContainer = this.productCard.querySelector('.product-card-grid__image-container');
    if (!imageContainer) return;

    const images = mediaArray.filter(media => media.media_type === 'image').slice(0, 2);
    
    if (images.length === 0) return;

    const mainImage = images[0];
    const hoverImage = images[1];

    const mainImageEl = imageContainer.querySelector('.product-card-grid__image:not(.product-card-grid__image--hover)');
    const hoverImageEl = imageContainer.querySelector('.product-card-grid__image--hover');

    if (mainImageEl && mainImage) {
      this.updateImageElement(mainImageEl, mainImage, productTitle);
    }

    if (hoverImage) {
      if (hoverImageEl) {
        this.updateImageElement(hoverImageEl, hoverImage, productTitle);
        hoverImageEl.style.display = '';
      } else {
        this.createHoverImage(imageContainer, hoverImage, productTitle);
      }
    } else if (hoverImageEl) {
      hoverImageEl.style.display = 'none';
    }
  }

  updateImageElement(imgElement, mediaData, productTitle) {
    const preview = mediaData.preview_image || mediaData;
    const imageSrc = preview.src || '';
    
    if (!imageSrc) return;

    const imageBase = this.getImageBase(imageSrc);
    const alt = preview.alt || productTitle || '';

    imgElement.src = this.getImageUrl(imageBase, '400x400');
    imgElement.srcset = `
      ${this.getImageUrl(imageBase, '200x200')} 200w,
      ${this.getImageUrl(imageBase, '400x400')} 400w,
      ${this.getImageUrl(imageBase, '600x600')} 600w
    `.trim();
    imgElement.alt = alt;
  }

  createHoverImage(container, mediaData, productTitle) {
    const preview = mediaData.preview_image || mediaData;
    const imageSrc = preview.src || '';
    
    if (!imageSrc) return;

    const imageBase = this.getImageBase(imageSrc);
    const alt = preview.alt || productTitle || '';

    const hoverImg = document.createElement('img');
    hoverImg.className = 'product-card-grid__image product-card-grid__image--hover';
    hoverImg.src = this.getImageUrl(imageBase, '400x400');
    hoverImg.srcset = `
      ${this.getImageUrl(imageBase, '200x200')} 200w,
      ${this.getImageUrl(imageBase, '400x400')} 400w,
      ${this.getImageUrl(imageBase, '600x600')} 600w
    `.trim();
    hoverImg.sizes = '(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw';
    hoverImg.alt = alt;
    hoverImg.width = 400;
    hoverImg.height = 400;
    hoverImg.loading = 'lazy';

    container.appendChild(hoverImg);
  }

  getImageBase(imageSrc) {
    return imageSrc.split('?')[0];
  }

  getImageUrl(imageBase, size) {
    const extension = imageBase.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    if (!extension) return imageBase;
    
    return imageBase.replace(
      new RegExp(`\\.${extension[1]}$`, 'i'),
      `_${size}_crop_center.${extension[1]}`
    );
  }

  updateSelectedState() {
    this.querySelectorAll('.quick-add-color-picker__color').forEach(colorEl => {
      const input = colorEl.querySelector('input[data-color-option]');
      if (input && input.checked) {
        colorEl.classList.add('is-selected');
      } else {
        colorEl.classList.remove('is-selected');
      }
    });
  }

  parseJSON(jsonString) {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn('Failed to parse JSON:', e);
      return null;
    }
  }
}

if (!customElements.get('quick-add-color-picker')) {
  customElements.define('quick-add-color-picker', QuickAddColorPicker);
}



if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.moved = false; // Track if modal has been moved to body

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      // Override connectedCallback to prevent moving to body on page load
      connectedCallback() {
        // Don't move to body immediately - only when shown
        // This prevents Safari crashes with multiple featured collections
        return;
      }

      // Move modal to body only when it's actually shown
      moveToBody() {
        if (this.moved) return;
        this.moved = true;
        document.body.appendChild(this);
      }

      disableScroll() {
        document.body.classList.add('overflow-hidden');
      }
      enableScroll() {
        document.body.classList.remove('overflow-hidden');
      }
      show(opener) {
        // Move to body only when showing
        this.moveToBody();
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const loadingSpinner = opener.querySelector('.loading__spinner');
        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // Set display first, then trigger animation on next frame
            this.classList.add('is-opening');
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // super.show() will set the 'open' attribute and handle overflow
                super.show(opener);
                this.disableScroll();
              });
            });
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            const loadingSpinner = opener.querySelector('.loading__spinner');
            if (loadingSpinner) {
              loadingSpinner.classList.add('hidden');
            }
          });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        
        if (preventFocus) this.openedBy = null;
        
        this.enableScroll();
        
        // Remove the 'open' attribute to trigger close animation
        this.removeAttribute('open');
        
        // Wait for animation to complete before hiding with display: none
        setTimeout(() => {
          this.classList.remove('is-opening');
          this.modalContent.innerHTML = '';
          document.body.classList.remove('overflow-hidden');
          document.body.dispatchEvent(new CustomEvent('modalClosed'));
          removeTrapFocus(this.openedBy);
          window.pauseAllMedia();
        }, 600); // Match --duration-long (600ms) from CSS
      }

      preprocessHTML(productElement) {
        console.log(productElement);
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.disableSwiperGallery(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      disableSwiperGallery(productElement) {
        // Find all elements with data-swiper-slideshow attribute (the divs inside swiper-slideshow custom elements)
        const swiperContainers = productElement.querySelectorAll('[data-swiper-slideshow]');
        
        swiperContainers.forEach(container => {
          container.removeAttribute('data-swiper-slideshow');
          console.log('Removed data-swiper-slideshow from:', container);
        });
        
        // Verify it's gone
        const remaining = productElement.querySelectorAll('[data-swiper-slideshow]');
        console.log('Remaining swiper containers after removal:', remaining.length);
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 700px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
